import Player from '@vimeo/player';
import { urlify } from './string';

let youtubeAPIReady = false;
const videos = {};

const whichService = (src, uid) => {
	if (videos[`${src}-${uid}`] && videos[`${src}-${uid}`].service) return videos[`${src}-${uid}`].service;
	return (urlify(src).indexOf('youtube') !== -1 ? 'youtube' : 'vimeo');
};

const extractEmbedSource = (src, uid) => {
	if (/<iframe([^>]*)/.test(src)) {
		// Break apart.
		const iframeValues = src.match(/<iframe([^>]*)/)[1].replace(/["\\]/g, '').split(' ').reduce((acc, attr) => {
			// Skip empty arrays
			if (!/\S/.test(attr)) return acc;
			return { ...acc, [attr.split('=')[0]]: attr.split('=')[1] };
		}, {});

		src = iframeValues.src || '';

		if (src.indexOf('?') !== -1) src = src.slice(0, src.indexOf('?'));
	}

	const service = whichService(src, uid);

	src = src.split('/').slice(-1).join('');
	if (src.indexOf('?') !== -1) {
		src.slice(src.indexOf('?') + 1)
			.split('&')
			.map((segment) => segment.split('='))
			.forEach((segment) => {
				if (segment[0] === 'v') src = segment[1];
			});
	}

	if (!videos[`${src}-${uid}`]) {
	videos[`${src}-${uid}`] = {
		service,
	};
	}

	return src;
};

const initYoutube = (src, uid, { autoplay = 0, loop = 0, mute = 0 }, { width = 560, height = 315 }) => {
	let renderReadyTimeout = null;
	if (!window.YT && typeof window.YT !== 'object') {
		// Initialize youtube API script.
		const script = document.createElement('script');
		script.id = 'youtube-iframe-api';
		script.src = 'https://www.youtube.com/iframe_api';
		document.head.append(script);

		window.onYouTubeIframeAPIReady = () => {
			youtubeAPIReady = true;
		};
	}

	// https://developers.google.com/youtube/player_parameters.html?playerVersion=HTML5

	const options = {
		fs: 0,
		controls: 0,
		disablekb: 1,
		autoplay,
		loop,
		mute,
		playlist: src,
		modestbranding: 1,
		playsinline: 1,
		showinfo: 0,
		autohide: 0,
		cc_load_policy: 0,
		iv_load_policy: 3,
		enablejsapi: 1,
		origin: window.location.origin,
	};

	const renderEmbed = () => {
		if (!youtubeAPIReady) {
			renderReadyTimeout = setTimeout(renderEmbed, 200);
		} else if (!videos[`${src}-${uid}`].ref) {
			// Doesn't generate a new ref for every instance only every video src
			videos[`${src}-${uid}`].ref = new window.YT.Player(`iframe-${src}-${uid}`, {
				videoId: src,
				playerVars: options,
				width,
				height,
			});
			clearTimeout(renderReadyTimeout);
		}
	};


	renderEmbed();
};

const initVimeo = (src, uid, { autoplay = 0, loop = 0, mute = 0 }, { width = 560, height = 315 }) => {
	const options = {
		id: src,
		autoplay,
		loop,
		muted: mute,
		controls: false,
		portrait: false,
		title: false,
		width,
		height,
	};

	if (videos[`${src}-${uid}`] && !videos[`${src}-${uid}`].ref) {
		videos[`${src}-${uid}`].ref = new Player(`iframe-${src}-${uid}`, options);
	}
};

const initVideo = (src, uid, options = {}, settings = {}) => {
	if (videos[`${src}-${uid}`].service === 'youtube') initYoutube(src, uid, options, settings);
	else initVimeo(src, uid, options, settings);
};

const destroyVideo = (src, uid) => {
	delete videos[`${src}-${uid}`].ref;
};

const dimensionsVideo = (src, uid) => {
	if (videos[`${src}-${uid}`].service === 'youtube') return Promise.reject(new Error('YouTube dimensions unavailable.'));
	return Promise.all([videos[`${src}-${uid}`].ref.getVideoWidth(), videos[`${src}-${uid}`].ref.getVideoHeight()]).then((dimensions) => ({ width: dimensions[0], height: dimensions[1] }));
};

// Pause
const pauseVideo = (src, uid) => (videos[`${src}-${uid}`].service === 'youtube' ? videos[`${src}-${uid}`].ref.pauseVideo() : videos[`${src}-${uid}`].ref.pause());
const isVideoPaused = (src, uid) => {
	if (videos[`${src}-${uid}`].service === 'youtube') return new Promise((resolve) => resolve(videos[`${src}-${uid}`].ref.getPlayerState() !== 1));
	return videos[`${src}-${uid}`].ref.getPaused();
};

// Play
const playVideo = (src, uid) => (videos[`${src}-${uid}`].service === 'youtube' ? videos[`${src}-${uid}`].ref.playVideo() : videos[`${src}-${uid}`].ref.pause());
const isVideoPlaying = (src, uid) => {
	if (videos[`${src}-${uid}`].service === 'youtube') return new Promise((resolve) => resolve(videos[`${src}-${uid}`].ref.getPlayerState() === 1));
	return videos[`${src}-${uid}`].ref.getPaused().then((paused) => !paused);
};

// Unmute
const muteVideo = (src, uid) => (videos[`${src}-${uid}`].service === 'youtube' ? videos[`${src}-${uid}`].ref.mute() : videos[`${src}-${uid}`].ref.setMuted(true));
const unMuteVideo = (src, uid) => (videos[`${src}-${uid}`].service === 'youtube' ? videos[`${src}-${uid}`].ref.unMute() : videos[`${src}-${uid}`].ref.setMuted(false));
const isVideoMuted = (src, uid) => {
	if (videos[`${src}-${uid}`].service === 'youtube') return new Promise((resolve) => resolve(videos[`${src}-${uid}`].ref.isMuted()));
	return videos[`${src}-${uid}`].ref.getMuted();
};

export default initVideo;
export { whichService, pauseVideo, isVideoPaused, playVideo, isVideoPlaying, muteVideo, unMuteVideo, isVideoMuted, extractEmbedSource, destroyVideo, dimensionsVideo };
